import React, { useState, useEffect } from 'react';
import { trackWhatsAppClick } from '../utils/tracking';
import { toWhatsAppPhone } from '../utils/whatsappPhone';
import { withOriginCode } from '../utils/siteEvents';
import styled from 'styled-components';
import { FiTarget, FiEye, FiHeart, FiMail } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import WhatsAppIcon from '../components/icons/WhatsAppIcon';
import { useSettings } from '../contexts/SettingsContext';
import Seo, { organizationSchema } from '../components/Seo';
import { buildImageUrl, brokerService } from '../services/api';
import {
  Container, Section, SectionHeader, Eyebrow, SectionTitle, SectionSubtitle, Badge, Spinner,
} from '../components/ui';

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Hero = styled.div<{ $hasImage?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $hasImage }) => ($hasImage ? '1.1fr 0.9fr' : '1fr')};
  gap: 3rem;
  align-items: center;
  padding-top: 3.5rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const HeroText = styled.div`
  max-width: ${({ theme }) => theme.fonts && '60ch'};

  h1 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: clamp(2rem, 4vw, 2.75rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.colors.foreground};
    margin: 0.75rem 0 1.25rem;
  }
  p {
    color: ${({ theme }) => theme.colors.neutral[700]};
    line-height: 1.8;
    font-size: 1.05rem;
    white-space: pre-line;
  }
`;

const HeroImg = styled.div`
  img {
    width: 100%;
    height: 440px;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.borderRadius.xl};
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  @media (max-width: 860px) {
    img { height: 280px; }
  }
`;

const Values = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 3.5rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 1.75rem;

  .icon {
    width: 52px;
    height: 52px;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.primarySoft};
    color: ${({ theme }) => theme.colors.primaryDark};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.1rem;
  }
  h3 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.2rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.foreground};
    margin-bottom: 0.5rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.65;
    white-space: pre-line;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const BrokerCard = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 1.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.hover};
  }
`;

const BrokerTop = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Avatar = styled.div<{ $photo?: string }>`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $photo, theme }) =>
    $photo ? `url(${$photo}) center/cover` : theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primaryDark};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.3rem;
`;

const BrokerName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
`;
const BrokerRole = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.85rem;
  font-weight: 600;
`;
const BrokerCreci = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.78rem;
`;
const BrokerBio = styled.p`
  color: ${({ theme }) => theme.colors.neutral[600]};
  line-height: 1.6;
  font-size: 0.92rem;
  margin-bottom: 1.1rem;
`;

const BrokerActions = styled.div`
  display: flex;
  gap: 0.6rem;
`;

const ActionBtn = styled.a<{ $whatsapp?: boolean }>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.2s ease;

  ${({ $whatsapp, theme }) =>
    $whatsapp
      ? `background:#25d366;color:#fff;&:hover{background:#1ebe5b;}`
      : `background:${theme.colors.surface};color:${theme.colors.foreground};border:1px solid ${theme.colors.border};&:hover{border-color:${theme.colors.neutral[400]};}`}
`;

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface Broker {
  id: number;
  name: string;
  email: string;
  phone: string;
  creci: string;
  specialty?: string;
  experience_years: number;
  bio?: string;
  photo?: string;
  is_active: boolean;
  is_team_leader: boolean;
}

const getInitials = (name: string) =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
const getWhatsAppUrl = (broker: { name: string; phone: string }) =>
  `https://wa.me/${toWhatsAppPhone(broker.phone)}?text=${encodeURIComponent(
    withOriginCode(`Olá ${broker.name}! Gostaria de mais informações sobre imóveis disponíveis.`)
  )}`;

const AboutPage: React.FC = () => {
  const { siteSettings, loading } = useSettings();
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loadingBrokers, setLoadingBrokers] = useState(true);

  useEffect(() => {
    const loadBrokers = async () => {
      try {
        setLoadingBrokers(true);
        const response = await brokerService.getPublicActive();
        setBrokers(response);
      } catch (error) {
        console.error('Erro ao carregar corretores:', error);
      } finally {
        setLoadingBrokers(false);
      }
    };
    loadBrokers();
  }, []);

  if (loading) {
    return (
      <Page>
        <Header />
        <Loading>
          <Spinner $size={34} />
          <p>Carregando...</p>
        </Loading>
        <Footer />
      </Page>
    );
  }

  const aboutTitle = siteSettings?.about_title || 'Sobre nós';
  const aboutContent =
    siteSettings?.about_content ||
    `Somos uma imobiliária comprometida em oferecer as melhores soluções para nossos clientes.

Com anos de experiência e uma equipe qualificada, ajudamos você a encontrar o imóvel ideal com transparência, segurança e atendimento personalizado.`;
  const aboutImage = siteSettings?.about_image;
  const aboutMission =
    siteSettings?.about_mission ||
    'Conectar pessoas aos seus imóveis dos sonhos, com atendimento personalizado e soluções inovadoras.';
  const aboutVision =
    siteSettings?.about_vision ||
    'Ser a imobiliária de referência na região, reconhecida pela excelência e satisfação dos clientes.';
  const aboutValues =
    siteSettings?.about_values ||
    'Transparência, ética, comprometimento e inovação em todos os processos e relacionamentos.';

  return (
    <Page>
      <Seo
        title={`Sobre a ${siteSettings?.company_info?.name || siteSettings?.site_name || 'imobiliária'}`}
        description={aboutContent.replace(/\s+/g, ' ').slice(0, 158)}
        canonicalPath="/sobre"
        image={aboutImage ? (aboutImage.startsWith('http') ? aboutImage : buildImageUrl(aboutImage)) : undefined}
        structuredData={organizationSchema(siteSettings)}
      />
      <Header />

      <Container>
        <Hero $hasImage={!!aboutImage}>
          <HeroText>
            <Eyebrow>Quem somos</Eyebrow>
            <h1>{aboutTitle}</h1>
            <p>{aboutContent}</p>
          </HeroText>
          {aboutImage && (
            <HeroImg>
              <img
                src={aboutImage.startsWith('http') ? aboutImage : buildImageUrl(aboutImage)}
                alt={aboutTitle}
              />
            </HeroImg>
          )}
        </Hero>

        <Values>
          <ValueCard>
            <div className="icon"><FiTarget size={24} /></div>
            <h3>Nossa missão</h3>
            <p>{aboutMission}</p>
          </ValueCard>
          <ValueCard>
            <div className="icon"><FiEye size={24} /></div>
            <h3>Nossa visão</h3>
            <p>{aboutVision}</p>
          </ValueCard>
          <ValueCard>
            <div className="icon"><FiHeart size={24} /></div>
            <h3>Nossos valores</h3>
            <p>{aboutValues}</p>
          </ValueCard>
        </Values>
      </Container>

      <Section $muted style={{ marginTop: '4rem' }}>
        <Container>
          <SectionHeader $center>
            <Eyebrow>Equipe</Eyebrow>
            <SectionTitle>Conheça nossos corretores</SectionTitle>
            <SectionSubtitle style={{ textAlign: 'center' }}>
              Profissionais especializados, prontos para ajudar você a encontrar o imóvel ideal.
            </SectionSubtitle>
          </SectionHeader>

          {loadingBrokers ? (
            <Loading>
              <Spinner $size={30} />
              <p>Carregando equipe...</p>
            </Loading>
          ) : brokers.length > 0 ? (
            <TeamGrid>
              {brokers.map((broker) => (
                <BrokerCard key={broker.id}>
                  {broker.is_team_leader && (
                    <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                      <Badge $variant="brand">Líder</Badge>
                    </div>
                  )}
                  <BrokerTop>
                    <Avatar $photo={broker.photo ? buildImageUrl(broker.photo) : undefined}>
                      {!broker.photo && getInitials(broker.name)}
                    </Avatar>
                    <div>
                      <BrokerName>{broker.name}</BrokerName>
                      <BrokerRole>{broker.specialty || 'Corretor de imóveis'}</BrokerRole>
                      {broker.creci && <BrokerCreci>CRECI {broker.creci}</BrokerCreci>}
                    </div>
                  </BrokerTop>
                  <BrokerBio>
                    {broker.bio || 'Corretor especializado em ajudar você a encontrar o imóvel ideal.'}
                  </BrokerBio>
                  <BrokerActions>
                    <ActionBtn href={getWhatsAppUrl(broker)} target="_blank" rel="noopener noreferrer" $whatsapp onClick={() => trackWhatsAppClick('about_broker')}>
                      <WhatsAppIcon size={16} color="white" /> WhatsApp
                    </ActionBtn>
                    <ActionBtn href={`mailto:${broker.email}`}>
                      <FiMail size={15} /> E-mail
                    </ActionBtn>
                  </BrokerActions>
                </BrokerCard>
              ))}
            </TeamGrid>
          ) : (
            <Loading>
              <p>Nenhum corretor disponível no momento.</p>
            </Loading>
          )}
        </Container>
      </Section>

      <Footer />
    </Page>
  );
};

export default AboutPage;
